import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import apiClient from '../../lib/apiClient';
import {
  CursorArrowRaysIcon, BanknotesIcon, ChartBarSquareIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import './styles.css';

// ─── SVG Line Chart puro ─────────────────────────────────────────────────────
function LineChart({ dates, series }) {
  const W = 640, H = 200, PL = 40, PR = 16, PT = 16, PB = 36;
  const cW = W - PL - PR;
  const cH = H - PT - PB;
  const n = dates.length;
  if (n === 0) return null;

  const allVals = series.flatMap(s => s.data);
  const maxVal  = Math.max(...allVals, 1);

  const px = i => PL + (i / (n - 1)) * cW;
  const py = v => PT + cH - (v / maxVal) * cH;

  const mkPath = data =>
    data.map((v, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join(' ');

  const yTicks = [0, Math.round(maxVal / 2), maxVal];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="cd-chart" preserveAspectRatio="xMidYMid meet">
      {/* Grid lines */}
      {yTicks.map(tick => (
        <line
          key={tick}
          x1={PL} y1={py(tick).toFixed(1)}
          x2={W - PR} y2={py(tick).toFixed(1)}
          stroke="rgba(255,255,255,0.07)"
          strokeDasharray="4 4"
        />
      ))}
      {/* Y axis labels */}
      {yTicks.map(tick => (
        <text key={tick} x={PL - 6} y={py(tick) + 4} textAnchor="end" className="cd-chart__tick">
          {tick}
        </text>
      ))}
      {/* X axis labels */}
      {dates.map((d, i) => {
        const show = n <= 7 || i % 2 === 0;
        const label = d.slice(5); // MM-DD
        return show ? (
          <text key={i} x={px(i)} y={H - 6} textAnchor="middle" className="cd-chart__tick">
            {label}
          </text>
        ) : null;
      })}
      {/* Series lines */}
      {series.map(s => (
        <React.Fragment key={s.key}>
          <path
            d={mkPath(s.data)}
            fill="none"
            stroke={s.color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Dots */}
          {s.data.map((v, i) => (
            <circle
              key={i}
              cx={px(i)}
              cy={py(v)}
              r="4"
              fill={s.color}
              stroke="rgba(10,14,46,0.8)"
              strokeWidth="1.5"
            />
          ))}
        </React.Fragment>
      ))}
    </svg>
  );
}

// ─── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color }) {
  return (
    <div className="cd-stat glass-card">
      <div className="cd-stat__icon" style={{ color }}>{icon}</div>
      <div className="cd-stat__body">
        <span className="cd-stat__value" style={{ color }}>{value}</span>
        <span className="cd-stat__label">{label}</span>
      </div>
    </div>
  );
}

// ─── Exportar CSV client-side ─────────────────────────────────────────────────
function exportCSV(dates, clicks, sales, campaignId) {
  const rows = [['Data', 'Cliques', 'Vendas']];
  dates.forEach((d, i) => rows.push([d, clicks[i] ?? 0, sales[i] ?? 0]));
  const csv = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `campanha_${campaignId}_performance.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function CampaignDashboardContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [camp, setCamp] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [campRes, perfRes] = await Promise.all([
          apiClient.get(`/campaign/${id}`),
          apiClient.get(`/campaign/${id}/performance`),
        ]);
        setCamp(campRes.data.campaign);
        setPerf(perfRes.data.performance);
      } catch (err) {
        setError(err.response?.data?.message || 'Erro ao carregar dados da campanha');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleClone = async () => {
    setCloning(true);
    try {
      await apiClient.post(`/campaign/${id}/clone`);
      navigate('/campaign-launch');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao clonar campanha');
    } finally {
      setCloning(false);
    }
  };

  if (loading) return (
    <div className="cd-loading">
      <div className="cd-loading__robot"><ChartBarSquareIcon style={{ width: 32, height: 32 }} /></div>
      <p>Carregando dados de performance...</p>
    </div>
  );

  if (error) return (
    <div className="cd-error glass-card">
      <p>{error}</p>
      <button className="neon-button" onClick={() => navigate(-1)}>Voltar</button>
    </div>
  );

  const dates  = perf?.dates  || [];
  const clicks = perf?.clicks || [];
  const sales  = perf?.sales  || [];

  const totalClicks = clicks.reduce((s, v) => s + v, 0);
  const totalSales  = sales.reduce((s, v) => s + v, 0);
  const convRate    = totalClicks > 0
    ? ((totalSales / totalClicks) * 100).toFixed(1)
    : '0.0';

  const series = [
    { key: 'clicks', label: 'Cliques', data: clicks, color: '#06B6D4' },
    { key: 'sales',  label: 'Vendas',  data: sales,  color: '#7E22CE' },
  ];

  return (
    <div className="campaign-dashboard">
      {/* Header */}
      <div className="cd-header glass-card">
        <div className="cd-header__info">
          <h2 className="gradient-text">{camp?.product_name || 'Campanha'}</h2>
          <p className="cd-header__sub">{camp?.business_name} · {camp?.niche}</p>
        </div>
        <div className="cd-header__badges">
          <span className={`neon-badge cd-status cd-status--${camp?.status}`}>
            {camp?.status === 'active' ? '● Ativa' : camp?.status}
          </span>
          {camp?.plan_name && (
            <span className="neon-badge">{camp.plan_name}</span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="cd-stats">
        <StatCard label="Total de Cliques"   value={totalClicks} icon={<CursorArrowRaysIcon style={{ width: 20, height: 20 }} />} color="#06B6D4" />
        <StatCard label="Total de Vendas"    value={totalSales}  icon={<BanknotesIcon style={{ width: 20, height: 20 }} />} color="#7E22CE" />
        <StatCard label="Taxa de Conversão"  value={`${convRate}%`} icon={<ChartBarSquareIcon style={{ width: 20, height: 20 }} />} color="#1E40AF" />
        <StatCard label="Dias Ativos"        value={dates.length}   icon={<CalendarDaysIcon style={{ width: 20, height: 20 }} />} color="#8B5CF6" />
      </div>

      {/* Chart */}
      <div className="cd-chart-card glass-card">
        <div className="cd-chart-card__header">
          <h3>Performance dos Últimos {dates.length} Dias</h3>
          <div className="cd-chart-card__legend">
            {series.map(s => (
              <span key={s.key} className="cd-legend-item">
                <span className="cd-legend-dot" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        {dates.length > 0
          ? <LineChart dates={dates} series={series} />
          : <p className="cd-chart-card__empty">Ainda sem dados de performance</p>
        }
      </div>

      {/* Headline preview */}
      {camp?.headline && (
        <div className="cd-headline glass-card">
          <span className="neon-badge">Headline da Campanha</span>
          <p>{camp.headline}</p>
        </div>
      )}

      {/* Actions */}
      <div className="cd-actions">
        <button
          className="cl-btn cl-btn--ghost"
          onClick={() => exportCSV(dates, clicks, sales, id)}
        >
          Exportar Leads CSV
        </button>
        <button
          className="neon-button"
          onClick={handleClone}
          disabled={cloning}
        >
          {cloning ? 'Clonando...' : '↺ Repetir Campanha'}
        </button>
      </div>
    </div>
  );
}

function CampaignDashboardPage() {
  return (
    <AppLayout
      title="Performance da Campanha"
      subtitle="Análise detalhada dos resultados"
      fullWidth
    >
      <CampaignDashboardContent />
    </AppLayout>
  );
}

export default CampaignDashboardPage;
